/**
 * @file: index.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Platform } from 'react-native';
import { kAppVersion } from './app-info';
import configRaw from './config.json';

export type EnvType = 'dev' | 'production' | 'staging';
export type EnvTagType = 'd' | 'p' | 's' | 't';
export type ReleaseTarget = 'mobile' | 'tv';

const EnvTagMap = new Map<EnvType, EnvTagType>([
  ['dev', 'd'],
  ['production', 'p'],
  ['staging', 't'],
]);

export type AppConfig = {
  bundleId: string;
  packageName: string;
  /** the distribution name of the app */
  appDistName: string;
  apiEndpoint: string;
  aiEndpoint: string;
  chatEndpoint: string;
  wsEndPoint: string;
  embeddedWebUrl: string;
  /** The poster studio's origin, framed by the 朋友圈海报 page. */
  posterStudioUrl: string;
  requestTimeout?: number;
  environment: EnvType;
  target: ReleaseTarget;
  version: string;
  cloudPush: {
    enabled: boolean;
  };
  linking: {
    url: string;
    scheme: string;
  };
  sentry: {
    enabled: boolean;
    dsn: string;
    tracesSampleRate: number;
  };
  mixpanel: {
    token: string;
  };
  weixinOpen: {
    appId: string;
    linking: string;
  };
  wework: {
    urlScheme: string;
  };
  ota: {
    updateUrl: string;
  };
};

export function envTypeToTag(envType: EnvType): EnvTagType {
  return EnvTagMap.get(envType)!;
}

export const config = { ...configRaw } as Omit<AppConfig, 'environment'> & {
  environment: EnvType;
};

if (__DEV__ && Platform.OS === 'android') {
  config.apiEndpoint = config.apiEndpoint?.replace('https://', 'http://');
  config.aiEndpoint = config.aiEndpoint?.replace('https://', 'http://');
}

if (!__DEV__ && Platform.OS === 'web') {
  // use the same domain as the api endpoint for non development build
  // the backend will provide the reverse proxy setup for the api requests.
  config.apiEndpoint = '/';
}

if (config.target == null) {
  // default target to mobile
  config.target = 'mobile';
}

config.version = kAppVersion;

/**
 * Get either the bundle id for ios/web or package name for android.
 * @description because we don't support tv build for ios, we have to isolate
 *   the application identifier between ios and android.
 * @returns app bundle id or package name
 */
export const getApplicationIdentifier = () => {
  return (
    (Platform.OS === 'android' ? config.packageName : config.bundleId) ??
    config.bundleId
  );
};
